import React from 'react';
import { Alert, AlertTitle, Box, Button, Stack, Typography } from '@mui/material';
import { isChunkLoadError, reloadOnce } from '../utils/chunkReload';

/**
 * Catches render errors so a component failure shows a message, not a blank page.
 *
 * Without a boundary anywhere in the tree, React unmounts everything on an
 * uncaught error and the user is left staring at white with nothing to act on
 * and nothing to report. That is exactly how the 2026-08-07 stale-bundle
 * failure presented, and the missing diagnosis cost more than the bug did.
 *
 * A chunk-load error is handled differently from an application error: it means
 * the tab is out of date rather than broken, so it reloads itself once. If the
 * reload has already been tried, it falls through to the same visible message
 * rather than looping.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, stale: false };
  }

  static getDerivedStateFromError(error) {
    return { error, stale: isChunkLoadError(error) };
  }

  componentDidCatch(error, errorInfo) {
    if (isChunkLoadError(error) && reloadOnce()) {
      // Reloading; no need to log a stale-bundle error as an application fault.
      return;
    }
    // Keep the real stack in the console: this is the thing whose absence made
    // the white page undiagnosable from the outside.
    console.error('Unhandled UI error:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { error, stale } = this.state;

    if (!error) {
      return this.props.children;
    }

    return (
      <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
        <Alert severity={stale ? 'info' : 'error'}>
          <AlertTitle>
            {stale ? 'This page is out of date' : 'Something went wrong'}
          </AlertTitle>

          {stale ? (
            <Typography variant="body2">
              The application was updated while this tab was open, so part of it
              could not load. Reloading picks up the current version. Nothing you
              have saved is affected.
            </Typography>
          ) : (
            <Typography variant="body2">
              This part of the page failed to load. Reloading usually clears it.
              If it keeps happening, the details below are what to report.
            </Typography>
          )}

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button variant="contained" size="small" onClick={this.handleReload}>
              Reload
            </Button>
          </Stack>

          {!stale && (
            <Box
              component="pre"
              sx={{
                mt: 2,
                p: 1,
                fontSize: '0.75rem',
                whiteSpace: 'pre-wrap',
                overflowX: 'auto',
                bgcolor: 'action.hover',
                borderRadius: 1,
              }}
            >
              {error.message || String(error)}
            </Box>
          )}
        </Alert>
      </Box>
    );
  }
}

export default ErrorBoundary;
